'use client';
import { useId as useNewsletterId } from 'react';
import { CaptchaRequestMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import React from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { PiChats } from 'react-icons/pi';

export function Newsletter() {
  const seoNewsletterId = useNewsletterId();
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => setIsSubscribed(true),
  });

  const locale = useLocaleStore((s) => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <section id="newsletters" className={cn('my-10', 'ea-homepage-newsletter')}>
      <form
        className={cn(
          'flex flex-col gap-8 bg-[#333333] px-3 py-4 md:flex-row md:rounded-lg md:p-16',
          'ea-homepage-newsletter__form',
        )}
        onSubmit={handleSubmit}
      >
<CaptchaRequestMessage error={subscribeMutation.error} />
        <div
          className={cn(
            ' flex flex-row items-center justify-start space-x-4  md:pr-7',
            'ea-homepage-newsletter__form-icon-email',
          )}
        >
          <PiChats
            className={cn(
              'h-[88px] w-[88px] text-white',
              'ea-homepage-newsletter__form-icon-email-icon',
            )}
          />
          <div
            className={cn('flex flex-col gap-2 text-white', 'ea-homepage-newsletter__form-title')}
          >
            <h2
              id="newsletter-title"
              data-ea-visual-tag="h4"
              className={cn(' text-2xl font-bold', 'ea-homepage-newsletter__form-title-main')}
            >
              ODOBERAŤ NEWSLETTER
            </h2>
            <p className={cn('text-sm ', 'ea-homepage-newsletter__form-title-sub')}>
              Prihláste sa na odber newsletteru a získajty vždy tie najnovšie informácie
            </p>
          </div>
        </div>
        <div
          className={cn(
            ' flex flex-col justify-center space-y-3 ',
            'ea-homepage-newsletter__form-item-email',
          )}
        >
          <div className={cn('flex h-10 flex-row items-center')}>
            <Input
              aria-label={systemLabels('email')}
              id={`newsletter-email-${seoNewsletterId}`}
              type="email"
              name="email"
              placeholder={t('text183')}
              onChange={(e) => setEmail(e.target.value)}
              className={{
                wrapper: cn(
                  'border-none bg-input pr-1 text-muted-foreground',
                  'ea-homepage-newsletter__form-item-email-input',
                ),
              }}
              after={
                <Button
                  type="submit"
                  aria-labelledby="newsletter-title"
                  className={cn(
                    'bg-transparen shadow-none hover:bg-input',
                    'ea-homepage-newsletter__form-item-email-submit-button',
                  )}
                  disabled={email.length === 0 || !checked || isSubscribed}
                  isLoading={subscribeMutation.isPending}
                >
                  <FaArrowRight className="text-primary" />{' '}
                </Button>
              }
            />
          </div>
          {isSubscribed && <span className={cn('text-success')}>{t('text234')}</span>}
          <div
            className={cn(
              'flex flex-row items-center space-x-2',
              'ea-homepage-newsletter__form-item-gdpr',
            )}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => setChecked(!checked)}
              id={`newsletter-gdpr-${seoNewsletterId}`}
              aria-label={t('text264') + ' ' + t('text265') + ' ' + t('text436')}
              className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
              required
            />
            <Label
              htmlFor={`newsletter-gdpr-${seoNewsletterId}`}
              className={cn('', 'ea-homepage-newsletter__form-item-gdpr-checkbox-label')}
            >
              <span
                className={cn('text-white', 'ea-homepage-newsletter__form-item-gdpr-checkbox-span')}
              >
                {t('text264')}&nbsp;
                <a
                  href={t('text391')}
                  className={cn(
                    'text-primary',
                    'ea-homepage-newsletter__form-item-gdpr-checkbox-link1',
                  )}
                >
                  {t('text265')}
                </a>{' '}
                <a
                  href={t('text392')}
                  className={cn(
                    'text-primary',
                    'ea-homepage-newsletter__form-item-gdpr-checkbox-link2',
                  )}
                >
                  {t('text436')}
                </a>
                .
              </span>
            </Label>
          </div>
        </div>
      </form>
    </section>
  );
}
